import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  FileText, ArrowLeft, ChevronDown, ChevronRight, Calculator,
  Database, Clock, User, DollarSign
} from 'lucide-react';

export default function TributarioMemoria() {
  const [loading, setLoading] = useState(true);
  const [memoria, setMemoria] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apuracaoId = searchParams.get('id');

  useEffect(() => {
    if (apuracaoId) {
      loadMemoria();
    }
  }, [apuracaoId]);

  const loadMemoria = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/tributario/apuracoes/${apuracaoId}/memoria`);
      setMemoria(response.data);
    } catch (error) {
      console.error('Erro ao carregar memória:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[600px]" />
      </div>
    );
  }

  if (!memoria) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Selecione uma apuração para ver a memória de cálculo.</p>
            <Button className="mt-4" onClick={() => navigate('/tributario/apuracao')}>
              Ir para Apurações
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const TributoCard = ({ titulo, dados, cor }) => (
    <Collapsible open={expandedSections[titulo]} onOpenChange={() => toggleSection(titulo)}>
      <Card className={`bg-${cor}-50 border-${cor}-200`}>
        <CollapsibleTrigger className="w-full">
          <CardHeader className="flex flex-row items-center justify-between cursor-pointer">
            <div>
              <CardTitle className="text-lg">{titulo}</CardTitle>
              <CardDescription>
                Valor: <strong className={`text-${cor}-700`}>
                  R$ {(dados?.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </strong>
              </CardDescription>
            </div>
            {expandedSections[titulo] ? (
              <ChevronDown className="w-5 h-5" />
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">
            {/* Fórmula */}
            <div className="p-3 bg-white rounded-lg">
              <p className="text-sm font-medium mb-1">Fórmula:</p>
              <code className="text-sm bg-gray-100 px-2 py-1 rounded">{dados?.formula}</code>
            </div>

            {/* Cálculo Detalhado */}
            {dados?.calculo_detalhado && (
              <div className="p-3 bg-white rounded-lg">
                <p className="text-sm font-medium mb-2">Cálculo Detalhado:</p>
                {Array.isArray(dados.calculo_detalhado) ? (
                  <ul className="text-sm space-y-1">
                    {dados.calculo_detalhado.map((linha, i) => (
                      <li key={i} className="text-muted-foreground">{linha}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">{dados.calculo_detalhado}</p>
                )}
              </div>
            )}

            {/* Fundamento Legal */}
            {dados?.fundamento_legal && (
              <div className="p-3 bg-white rounded-lg">
                <p className="text-sm font-medium mb-1">Fundamento Legal:</p>
                <p className="text-sm text-muted-foreground">{dados.fundamento_legal}</p>
              </div>
            )}

            {/* Observação */}
            {dados?.observacao && (
              <div className="p-3 bg-white rounded-lg">
                <p className="text-sm font-medium mb-1">Observação:</p>
                <p className="text-sm text-muted-foreground">{dados.observacao}</p>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );

  return (
    <div className="p-6 space-y-6" data-testid="tributario-memoria">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/tributario/apuracao')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6" />
            Memória de Cálculo
          </h1>
          <p className="text-muted-foreground">
            Competência: {memoria.competencia} | Tipo: {memoria.tipo}
          </p>
        </div>
      </div>

      {/* Metadados */}
      <div className="flex flex-wrap gap-4">
        <Badge variant="outline" className="gap-1">
          <Clock className="w-3 h-3" />
          {memoria.gerado_em ? new Date(memoria.gerado_em).toLocaleString('pt-BR') : '-'}
        </Badge>
        <Badge variant="outline" className="gap-1">
          <User className="w-3 h-3" />
          {memoria.gerado_por}
        </Badge>
        <Badge variant="outline" className="gap-1">
          <Database className="w-3 h-3" />
          Período: {memoria.periodo?.inicio} a {memoria.periodo?.fim}
        </Badge>
      </div>

      {/* Receitas */}
      <Collapsible open={expandedSections.receitas} onOpenChange={() => toggleSection('receitas')}>
        <Card>
          <CollapsibleTrigger className="w-full">
            <CardHeader className="flex flex-row items-center justify-between cursor-pointer">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  Receitas do Período
                </CardTitle>
                <CardDescription>
                  Base Tributável: <strong className="text-green-700">
                    R$ {(memoria.receitas?.base_tributavel || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </strong>
                </CardDescription>
              </div>
              {expandedSections.receitas ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Receita Faturada:</span>
                    <strong>R$ {(memoria.receitas?.faturada || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span>Receita Recebida:</span>
                    <strong>R$ {(memoria.receitas?.recebida || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span>Descontos:</span>
                    <strong className="text-red-600">- R$ {(memoria.receitas?.descontos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span>Inadimplência:</span>
                    <strong className="text-orange-600">R$ {(memoria.receitas?.inadimplencia || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
                  </div>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2">Origem dos Dados</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>Coleção: {memoria.receitas?.detalhamento?.origem_dados}</li>
                    <li>Filtro: {memoria.receitas?.detalhamento?.filtro_aplicado}</li>
                    <li>Campos: {memoria.receitas?.detalhamento?.campos_utilizados?.join(', ')}</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Tributos Detalhados */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TributoCard titulo="PIS" dados={memoria.tributos_detalhados?.pis} cor="blue" />
        <TributoCard titulo="COFINS" dados={memoria.tributos_detalhados?.cofins} cor="green" />
        <TributoCard titulo="ISS" dados={memoria.tributos_detalhados?.iss} cor="amber" />
        <TributoCard titulo="IRPJ" dados={memoria.tributos_detalhados?.irpj} cor="purple" />
        <TributoCard titulo="CSLL" dados={memoria.tributos_detalhados?.csll} cor="red" />
      </div>

      {/* Totais */}
      <Card className="bg-gray-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Totais Consolidados
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-white rounded-lg">
              <p className="text-sm text-muted-foreground">Tributos Federais</p>
              <p className="text-xl font-bold">
                R$ {(memoria.totais?.tributos_federais || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg">
              <p className="text-sm text-muted-foreground">Tributos Municipais</p>
              <p className="text-xl font-bold">
                R$ {(memoria.totais?.tributos_municipais || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="text-center p-4 bg-purple-100 rounded-lg">
              <p className="text-sm text-purple-600">Total de Tributos</p>
              <p className="text-xl font-bold text-purple-700">
                R$ {(memoria.totais?.total_tributos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg">
              <p className="text-sm text-muted-foreground">Carga Tributária</p>
              <p className="text-xl font-bold">
                {(memoria.totais?.carga_tributaria_faturamento || 0).toFixed(2)}%
              </p>
            </div>
          </div>

          {/* Fórmulas dos Totais */}
          <div className="mt-4 p-4 bg-white rounded-lg">
            <h4 className="font-medium mb-2">Fórmulas Utilizadas</h4>
            <ul className="text-sm space-y-1 text-muted-foreground">
              <li><strong>Total:</strong> {memoria.totais?.memoria_calculo?.formula_total}</li>
              <li><strong>Carga s/ Faturamento:</strong> {memoria.totais?.memoria_calculo?.formula_carga_fat}</li>
              <li><strong>Carga s/ Recebimento:</strong> {memoria.totais?.memoria_calculo?.formula_carga_rec}</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Parâmetros Utilizados */}
      <Card>
        <CardHeader>
          <CardTitle>Parâmetros Utilizados na Apuração</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {memoria.parametros && Object.entries(memoria.parametros).map(([key, value]) => (
              <div key={key} className="p-2 bg-muted rounded">
                <p className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                <p className="font-medium">
                  {typeof value === 'number' && value < 1 
                    ? `${(value * 100).toFixed(2)}%` 
                    : typeof value === 'number' 
                      ? value.toLocaleString('pt-BR')
                      : value
                  }
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
